import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import withStyles from '@material-ui/core/styles/withStyles';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Refresh from '@material-ui/icons/Refresh';
import LinearProgress from '@material-ui/core/LinearProgress';
import ReactTable from 'react-table';
import Button from 'components/UI/CustomButtons/Button';
import { TableLoader } from 'components/common/TableLoader';
import GridItem from 'components/UI/Grid/GridItem';
import GridContainer from 'components/UI/Grid/GridContainer';
import RouterContext from 'helpers/routerContext';
import Checkbox from '@material-ui/core/Checkbox';
import Moment from 'react-moment';
import WithPermission from 'components/User/WithPermission';
import Badge from 'components/UI/Badge/Badge';
import { adStatus, paidAdStatus } from 'config';
import moment from 'moment';
import RedirectFlashMessage from 'components/common/RedirectFlashMessage';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import { LoadingAnimation } from 'components/common/Loading';
import {
  getPaidAdDetailsUrl,
  getPaidAdTitle,
  getPaidAdDescription,
} from 'helpers/functions';
// material-ui icons
import ArrowForward from '@material-ui/icons/ArrowForward';
import Updated from '@material-ui/icons/EditOutlined';
import Agency from '@material-ui/icons/SupervisorAccountRounded';
import Person from '@material-ui/icons/Person';
import Check from '@material-ui/icons/Check';
import Done from '@material-ui/icons/Done';
import Visibility from '@material-ui/icons/Visibility';
import ImageMosaic from 'components/common/ImageMosaic/ImageMosaic';

class PaidAdReview extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
    };
  }

  /**
   * @description
   * Format the DATE received from the server.
   * @param {string} date - DATE received from the server.
   * @returns {Object} Converted date.
   */
  dateFormat = date => {
    const calendarStrings = {
      lastDay: '[Ayer ] LT',
      sameDay: '[Hoy ] LT',
      lastWeek: '[] dddd[, ] LT',
      sameElse: 'L',
    };
    // 2019-02-08T15:59-0500 date for testing
    return <Moment date={date} calendar={calendarStrings} />;
  };

  /**
   * @description
   * Format frequency and duration received from the server.
   *
   * @param {Object} param - Frequency or duration received from the server.
   * @returns {Object} Converted and translated frequency and duration.
   */
  changeFormatToString = ({ years, months, days, hours, minutes, seconds }) => {
    const { i18n } = this.props;
    moment.locale(i18n.language);
    return moment()
      .add({ seconds, minutes, hours, days, months, years })
      .fromNow(true);
  };

  /**
   * @description
   * Handle delete, publish, unpublish or review action.
   *
   * @param {string} action - Specific action.
   * @param {Array} selectedItems - Items ids.
   * @returns {undefined} - No returns value.
   */
  handleAction = (action, selectedItems) => {
    const { adReview, adPublish, data } = this.props;
    const adIds = selectedItems.map(
      item => data.paidAds.edges.find(edge => edge.node.id === item).node.ad.id,
    );

    if (action === 'review') {
      adReview(adIds);
    } else {
      adPublish(adIds);
    }

    this.setState({ selectedItems: [] });
  };

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = id => {
    const { selectedItems } = this.state;
    // if item is selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    // if all are selected
    if (selectedItems.length === edges.length) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: edges.map(item => item.node.id) });
    }
  };

  /**
   * @description
   * Render Review button.
   *
   * @param {Object} node - Ad from api.
   * @returns {*} Review button.
   */
  renderCellReview = node => {
    const { classes, t, adReview, adPublish } = this.props;

    const canPublishStatus = [
      adStatus.TO_REVIEW,
      adStatus.PUBLISHED_BY_MODERATOR,
      adStatus.PUBLISHED_BY_PROCESSOR,
      adStatus.UNPUBLISHED_BY_MODERATOR,
      adStatus.UNPUBLISHED_BY_PROCESSOR,
    ];

    return (
      <div>
        {node.ad && node.ad.isPublished && !node.ad.reviewed && (
          <WithPermission
            alt={
              <div className={classes.reviewCheck}>
                <span>
                  <b data-cy="adReviewedSpan">{t('ad.list.not_reviewed')}</b>
                </span>
              </div>
            }
            allowPermissions={['review_ad']}
          >
            <Button
              data-cy="adReviewButton"
              color="success"
              id={`ads-review-${node.ad.id}`}
              onClick={() => adReview([node.ad.id])}
            >
              <span>{t('common.review')}</span>
            </Button>
          </WithPermission>
        )}
        {node.ad &&
          !node.ad.isPublished &&
          canPublishStatus.includes(node.ad.status) && (
            <WithPermission
              alt={
                <div className={classes.reviewCheck}>
                  <span>
                    <b data-cy="adReviewedSpan">{t('ad.list.not_published')}</b>
                  </span>
                </div>
              }
              allowPermissions={['publish_ad']}
            >
              <Button
                data-cy="adPublishButton"
                color="primary"
                id={`ads-publish-${node.ad.id}`}
                onClick={() => adPublish([node.ad.id])}
              >
                <span>{t('common.publish')}</span>
              </Button>
            </WithPermission>
          )}
      </div>
    );
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;

    return (
      <div>
        <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
          {t(`paidad.status.${paidAdStatus.PENDING}`)}
        </h6>
        {node.ad &&
          (node.ad.status !== adStatus.PUBLISHED_BY_MODERATOR &&
          node.ad.status !== adStatus.PUBLISHED_BY_PROCESSOR ? (
            <p className={`${classes.cellStatus}`}>
              {t('paidad.list.paidad_status.pending_publish')}
            </p>
          ) : (
            !node.ad.reviewed && (
              <p className={`${classes.cellStatus}`}>
                {t('paidad.list.paidad_status.pending_review')}
              </p>
            )
          ))}
      </div>
    );
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table columns.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.list.headers.ad')}
          </span>
        ),
        accessor: 'ad',
        sortable: false,
        filterable: false,
        minWidth: 300,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.list.headers.paidad')}
          </span>
        ),
        accessor: 'auto',
        sortable: false,
        filterable: false,
        minWidth: 150,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.review.list.headers.date')}
          </span>
        ),
        accessor: 'dates',
        sortable: false,
        filterable: false,
        maxWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.list.headers.sales')}
          </span>
        ),
        accessor: 'sales',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.list.headers.commission')}
          </span>
        ),
        accessor: 'commission',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('paidad.list.headers.status')}</p>
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        maxWidth: 85,
      },
      {
        Header: (
          <div className={classes.headerField} style={{ textAlign: 'center' }}>
            {t('paidad.list.headers.actions')}
          </div>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 125,
        maxWidth: 300,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table rows.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return edges.map(({ node }) => ({
      id: node.id,
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      ad: (
        <React.Fragment>
          <WithPermission
            alt={
              node.ad && (
                <div
                  id={`ad-title-${node.ad.id}`}
                  data-cy="adTitle"
                  className={classes.adTitleContainer}
                >
                  {node.ad.readyImages && (
                    <ImageMosaic
                      images={node.ad.readyImages.map(({ id, urls }) => ({
                        id,
                        ...urls,
                      }))}
                    />
                  )}
                  <ul
                    className={classes.adCatBread}
                    style={{ color: '#217db2' }}
                  >
                    <li
                      data-cy="adCategory"
                      style={{ padding: '0', margin: 0 }}
                    >
                      {node.ad.subcategory.parentCategory &&
                        node.ad.subcategory.parentCategory.title}
                    </li>
                    <li className={classes.adIconBread}>
                      <ArrowForward color="disabled" />
                    </li>
                    <li
                      data-cy="adSubcategory"
                      style={{ padding: '0', margin: 0 }}
                    >
                      {node.ad.subcategory && node.ad.subcategory.title}
                    </li>
                  </ul>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      width: '100%',
                    }}
                  >
                    <div className={classes.idAndDate}>
                      <div className={classes.searchFieldContainer}>
                        <b data-cy={node.ad.id}>ID: {node.ad.id}</b>
                        <span className={classes.modificationDate}>
                          <Updated /> {node.ad.updateCount} •
                          <span style={{ marginLeft: 4 }}>
                            {node.ad.updatedOnToOrder &&
                              this.dateFormat(node.ad.updatedOnToOrder)}
                          </span>
                        </span>
                        {node.ad.flagCount > 0 && (
                          <span style={{ marginLeft: 8 }}>
                            <Badge color="danger">{node.ad.flagCount}</Badge>
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={classes.adTitle} style={{ fontSize: 12 }}>
                      {node.ad.title}
                    </p>
                  </div>
                </div>
              )
            }
            allowPermissions={['view_autopaidad', 'view_ownership_autopaidad']}
          >
            {node.ad && (
              <div
                style={{ padding: '8px 0' }}
                id={`ad-title-${node.ad.id}`}
                data-cy="adTitle"
              >
                {node.ad.readyImages && (
                  <ImageMosaic
                    images={node.ad.readyImages.map(({ id, urls }) => ({
                      id,
                      ...urls,
                    }))}
                  />
                )}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  <div className={classes.idAndDate}>
                    <b data-cy={node.ad.id}>
                      {getPaidAdTitle(node.id, node.paidAdType, t)}
                    </b>
                    {node.flagCount > 0 && (
                      <span style={{ marginLeft: 8 }}>
                        <Badge color="danger">{node.flagCount}</Badge>
                      </span>
                    )}
                    <p className={classes.modificationDate}>
                      <Updated /> {node.ad.updateCount} •
                      <span style={{ marginLeft: 4 }}>
                        {node.ad.updatedOnToOrder &&
                          this.dateFormat(node.ad.updatedOnToOrder)}
                      </span>
                    </p>
                  </div>
                  <ul
                    className={classes.adCatBread}
                    style={{ color: '#217db2' }}
                  >
                    <li
                      data-cy="adCategory"
                      style={{ padding: '0', margin: 0 }}
                    >
                      {node.ad.subcategory.parentCategory &&
                        node.ad.subcategory.parentCategory.title}
                    </li>
                    <li className={classes.adIconBread}>
                      <ArrowForward color="disabled" />
                    </li>
                    <li
                      data-cy="adSubcategory"
                      style={{ padding: '0', margin: 0 }}
                    >
                      {node.ad.subcategory && node.ad.subcategory.title}
                    </li>
                  </ul>
                  <a
                    className={['read', classes.adBtns, classes.titleBtn].join(
                      ' ',
                    )}
                    href={getPaidAdDetailsUrl(node.id, node.paidAdType)}
                    onClick={e => {
                      e.preventDefault();
                      this.handleDetailsPaidAd(node.id, node.paidAdType);
                    }}
                  >
                    <b data-cy={node.ad.id}>ID: {node.ad.id} </b>
                    {node.ad.title}
                  </a>
                </div>
              </div>
            )}
          </WithPermission>
        </React.Fragment>
      ),
      auto: (
        <div>
          <span className={classes.paidAdFrequencies}>
            {getPaidAdDescription(node, t)}
          </span>
          {node.owner &&
            (node.owner.fullname ? (
              <WithPermission
                alt={
                  node.owner && (
                    <div>
                      <Agency />
                      {node.owner.fullname}
                    </div>
                  )
                }
                allowPermissions={[
                  'view_useragency',
                  'view_ownership_useragency',
                ]}
              >
                <div>
                  <Button
                    data-cy="paidAdOwner"
                    simple
                    color="primary"
                    className={classes.adBtns}
                    style={{ margin: 0 }}
                    onClick={() => this.handleDetailsUser(node.owner.id)}
                  >
                    <Agency />
                    {node.owner.fullname}
                  </Button>
                </div>
              </WithPermission>
            ) : (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 15,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Agency />
                <span>{`${t('promoted_ad.form.fields.private_user')} (${
                  node.owner.fakeId
                })`}</span>
              </div>
            ))}
          <WithPermission
            alt={
              node.manager && (
                <div>
                  <Person />
                  {node.manager && node.manager.fullname}
                </div>
              )
            }
            allowPermissions={['view_agency']}
          >
            {node.manager && (
              <div>
                <Button
                  data-cy="paidAdManager"
                  simple
                  color="primary"
                  className={classes.adBtns}
                  style={{ margin: 0 }}
                  onClick={() => this.handleDetailsUser(node.manager.id)}
                >
                  <Person />
                  {node.manager && node.manager.fullname}
                </Button>
              </div>
            )}
          </WithPermission>
          <div style={{ color: 'black' }}>
            <span>
              {node.hasPromotion && <b>{t('paidad.list.promotion.ads')}</b>}
            </span>
            {node.hasPromotion && node.duration.months === 5 ? (
              <span>
                {node.duration.months === 5 && (
                  <b> - {t('paidad.list.promotion.months')}</b>
                )}
              </span>
            ) : (
              <span>
                {node.duration.months === 5 && (
                  <b>{t('paidad.list.promotion.months')}</b>
                )}
              </span>
            )}
          </div>
        </div>
      ),
      dates: (
        <>
          <div
            data-cy="paidAdStartDate"
            className={classes.adUserContainer}
            key={`paidad-start-date-${node.id}`}
          >
            <Moment date={node.startDate} format="DD/MM/YYYY" />
          </div>
          <div
            data-cy="paidAdEndDate"
            key={`paidad-end-date-${node.id}`}
            className={classes.adUserContainer}
          >
            <Moment date={node.endDate} format="DD/MM/YYYY" />
          </div>
        </>
      ),
      sales: (
        <div>
          <p data-cy="agencySales" key={`agency-sales-${node.id}`}>
            {`$ ${node.userPrice}`}
          </p>
        </div>
      ),
      commission: (
        <div>
          <p data-cy="agencyCommission" key={`agency-commission-${node.id}`}>
            {`$ ${node.commission}`}
          </p>
        </div>
      ),
      status: <span data-cy="status">{this.renderCellStatus(node)}</span>,
      actions: selectedItems.indexOf(node.id) === -1 &&
        node.status !== paidAdStatus.CANCELLED && (
          // we've added some custom button actions
          <div className="actions-right" style={{ textAlign: 'center' }}>
            {this.renderCellReview(node)}
          </div>
        ),
    }));
  };

  /**
   * @description
   * Redirect to ads/details route.
   *
   * @param {string} id - Ad id.
   * @param {string} paidAdType - Type.
   */
  handleDetailsPaidAd = (id, paidAdType) => {
    const history = this.context;
    const url = getPaidAdDetailsUrl(id, paidAdType);
    history.push(url);
  };

  render() {
    const {
      data,
      loading,
      fullLoading,
      classes,
      refresh,
      t,
      notification,
      closeNotification,
      failedIds,
    } = this.props;
    const { selectedItems } = this.state;

    const paidAds =
      data.paidAds && data.paidAds.edges.length > 0 ? data.paidAds.edges : [];

    const rows = this.renderRows(paidAds);
    const cols = this.renderColumns(paidAds);

    return (
      <React.Fragment>
        <RedirectFlashMessage />
        <Snackbar
          place="tc"
          color={notification.type === 'success' ? 'success' : 'danger'}
          message={Object.keys(notification).length > 0 && notification.message}
          open={Object.keys(notification).length > 0}
          closeNotification={closeNotification}
          close
        />

        <div className={classes.pageContainer}>
          <GridContainer justify="space-between" alignItems="center">
            <GridItem>
              <h3 className={classes.title}>{t('paidad.review.list.title')}</h3>
            </GridItem>
            <GridItem>
              <Button
                color="primary"
                simple
                className={classes.showFilters}
                onClick={() => refresh()}
                id="refresh"
                key="refresh"
              >
                <Refresh />
                {t('ad.filters.update')}
              </Button>
            </GridItem>
          </GridContainer>

          <div className={classes.contentContainer}>
            <div
              className={[classes.loadingLineContainer, classes.topLine].join(
                ' ',
              )}
            >
              {' '}
              {fullLoading && (
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.loadingLine,
                  }}
                />
              )}
            </div>

            <div
              className={[
                classes.groupActions,
                classes.loteActionsAnimation,
                selectedItems.length > 0 ? classes.showLote : classes.hideLote,
              ].join(' ')}
            >
              <WithPermission allowPermissions={['publish_ad']}>
                <Button
                  data-cy="publishLote"
                  color="success"
                  simple
                  onClick={() => this.handleAction('publish', selectedItems)}
                  id="publishAd"
                >
                  <Visibility />
                  {t('common.publish')}
                </Button>
              </WithPermission>
              <WithPermission allowPermissions={['review_ad']}>
                <Button
                  data-cy="reviewLote"
                  color="primary"
                  simple
                  onClick={() => this.handleAction('review', selectedItems)}
                  id="reviewAd"
                >
                  <Done />
                  {t('common.review')}
                </Button>
              </WithPermission>
            </div>

            <div style={{ marginTop: '20px' }}>
              {loading ? (
                <TableLoader />
              ) : (
                <ReactTable
                  data={rows}
                  columns={cols}
                  filterable
                  loading={loading}
                  showPagination={false}
                  pageSize={rows.length}
                  className={['-striped -highlight', classes.tbl].join(' ')}
                  defaultFilterMethod={(field, row) => {
                    const id = field.pivotId || field.id;
                    return row[id] !== undefined
                      ? String(row[id]).startsWith(field.value)
                      : true;
                  }}
                  SubComponent={() => (
                    <div className={classes.loading}>
                      <LoadingAnimation />
                    </div>
                  )}
                  getTrProps={(state, rowInfo) => ({
                    style: {
                      borderLeft:
                        rowInfo && failedIds.indexOf(rowInfo.original.id) !== -1
                          ? '4px solid red'
                          : '',
                    },
                  })}
                  noDataText={t('common.no_data')}
                  minRows={rows.length > 0 ? rows.length : 8}
                />
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

/**
 * PaidAdReview PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop paid ads query's results
 * @param loading - prop query or mutations status
 * @param fullLoading - prop query or mutations status
 * @param t - prop from HOC with string translations
 * @param i18 - prop from HOC with i18n data
 * @param failedIds - prop from HOC with string translations
 * @param notification - prop to know if notification will be showed or not
 * @callback closeNotification - callback to close notification
 * @callback refresh - callback to refresh page
 * @callback adReview - to Review the paid ad
 * @callback adPublish - to Publish the paid ad
 */

PaidAdReview.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  adReview: PropTypes.func.isRequired,
  adPublish: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  notification: PropTypes.instanceOf(Object).isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PaidAdReviewWrapper = props => <PaidAdReview {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PaidAdReviewWrapper),
);
